import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * TradeInAppraiserContext provides shared helper methods and state accessors
 * to all child step components, eliminating the need to plumb helpers through props.
 *
 * The provider receives a new helpers object as the appraiser renders. A plain
 * context value would therefore change identity on every render and retrigger
 * consumers' effects. The provider uses one stable Proxy instead: property
 * reads delegate through a mutable container to the latest helpers without
 * changing the context value or the function references returned to consumers.
 *
 * Exported (not just the hook) so class components can consume it via `static contextType`.
 */
export const TradeInAppraiserContext = createContext(null);

/**
 * Provider that wraps the trade-in appraiser step tree.
 * Accepts a `helpers` object (handlers plus state accessors) and exposes it via
 * a stable Proxy. The Proxy is intentionally kept for the lifetime of the
 * provider; `setHelpers` swaps the object it reads from on each render, while
 * cached function wrappers forward calls to the current implementation.
 *
 * Nesting is also supported: the Proxy implements the own-key traps required
 * for `{ ...outerCtx, someHelper: override }`, allowing a subtree to override
 * selected helpers without losing the rest of the parent context.
 *
 * @param {{ [key: string]: (...args: any[]) => any }} helpers
 *   All values MUST be functions. Non-function (scalar/object) values are
 *   NOT reactive — consumers won't re-render when they change.
 */
export function TradeInAppraiserContextProvider({ helpers, children }) {
  const { proxy, setHelpers } = useMemo(() => {
    const box = { current: helpers };
    // Keep function references stable for hook dependency arrays while still
    // calling the implementation from the latest helpers object.
    const wrapperCache = new Map();
    return {
      proxy: new Proxy(
        {},
        {
          get(_, prop) {
            const value = box.current[prop];
            // State values are read directly; only functions need stable wrappers.
            if (typeof value !== 'function') return value;
            if (!wrapperCache.has(prop)) {
              wrapperCache.set(prop, (...args) => box.current[prop](...args));
            }
            return wrapperCache.get(prop);
          },
          has(_, prop) {
            return prop in box.current;
          },
          // Both traps are needed for object spread to copy the current helper
          // keys when a nested provider creates an overridden helper set.
          ownKeys() {
            return Reflect.ownKeys(box.current);
          },
          getOwnPropertyDescriptor(_, prop) {
            if (!(prop in box.current)) return undefined;
            return { enumerable: true, configurable: true };
          },
        }
      ),
      setHelpers(h) {
        box.current = h;
      },
    };
  }, []);

  setHelpers(helpers); // called during render

  return <TradeInAppraiserContext.Provider value={proxy}>{children}</TradeInAppraiserContext.Provider>;
}

TradeInAppraiserContextProvider.propTypes = {
  helpers: PropTypes.shape({}).isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * Hook to consume TradeInAppraiser helpers from context.
 * Throws if used outside of TradeInAppraiserContextProvider.
 */
export function useTradeInAppraiserContext() {
  const context = useContext(TradeInAppraiserContext);
  if (!context) {
    throw new Error('useTradeInAppraiserContext must be used within a TradeInAppraiserContextProvider');
  }
  return context;
}
